import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Toast, ToastPosition } from '../../interfaces/toast.interface';
import { ToastService } from '../../services';

const DEFAULT_TOAST_DURATION = 5000;

@Component({
    selector: 'app-toast',
    templateUrl: './toast.component.html',
    styleUrls: ['./toast.component.scss'],
    animations: [
        trigger('toastEnterLeave', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-50px)' }),
                animate('150ms', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('150ms', style({ opacity: 0, transform: 'translateY(-25px)', filter: 'blur(5px)' }))]),
        ]),
    ],
    imports: [NgClass, TranslateModule],
})
export class ToastComponent implements OnInit {
    public toasts: { toastData: Toast; expires: number; timeoutId?: number }[] = [];
    private toastsMap: Map<string, { toastData: Toast; expires: number; timeoutId?: number }[]> = new Map();
    public position: ToastPosition = 'center';

    constructor(public toastService: ToastService) {}

    public ngOnInit(): void {
        this.toastService.registerToastComponent(this);
    }

    public checkForExpiredToasts(): void {
        for (let i = 0; i < this.toasts.length; i++) {
            const toast = this.toasts[i];

            if (!toast) {
                continue;
            }

            if (toast.expires < Date.now()) {
                this.removeToast(i);
                i--; // We need this since removeToast modifies the array
            }
        }
    }

    public showToast(toast: Toast): number {
        // Calculate the toast's duration
        let expiration = Date.now();
        let timeoutId: number | undefined;

        if (toast.position) {
            this.position = toast.position;
        }

        if (toast.duration) {
            expiration += toast.duration;

            // Timeout OK
            timeoutId = window.setTimeout(() => {
                this.checkForExpiredToasts();
            }, toast.duration);
        } else {
            if (toast.duration === 0) {
                expiration += Number.POSITIVE_INFINITY;
            } else {
                expiration += DEFAULT_TOAST_DURATION;

                // Timeout OK
                timeoutId = window.setTimeout(() => {
                    this.checkForExpiredToasts();
                }, DEFAULT_TOAST_DURATION);
            }
        }

        const newToast = { toastData: toast, expires: expiration, timeoutId: timeoutId };
        const toastKey = this.generateToastKeyFromParameters(toast);

        if (toast.allowDuplicates) {
            // If duplicates are allowed, show and add it to the list of toasts for its respective key
            this.toastsMap.set(toastKey, [...(this.toastsMap.get(toastKey) ?? []), newToast]);
            this.toasts.push(newToast);
        } else if (this.isToastDistinct(toast)) {
            // Otherwise, if it's a new toast, show it
            this.toastsMap.set(toastKey, [newToast]);
            this.toasts.push(newToast);
        } else {
            // Otherwise, extend the existing toast's duration
            const existingToastDuration = this.toastsMap.get(toastKey)?.[0]?.expires ?? 0;
            const extendedToastDuration = existingToastDuration > expiration ? existingToastDuration : expiration;
            const toastToUpdate = this.toasts
                .filter((activeToast) => activeToast !== undefined)
                .find((activeToast) => this.generateToastKeyFromParameters(activeToast!.toastData) === toastKey);

            if (toastToUpdate !== undefined) {
                toastToUpdate.expires = extendedToastDuration;
            }
        }

        return this.toasts.length - 1;
    }

    public removeToast(toastIndex: number): void {
        if (
            toastIndex < 0 ||
            toastIndex > this.toasts.length - 1 ||
            this.toasts[toastIndex] === undefined ||
            Object.keys(this.toasts[toastIndex] ?? {}).length === 0
        ) {
            return;
        }

        clearTimeout(this.toasts[toastIndex].timeoutId); // There's no exception if an invalid id is passed
        this.toastsMap.delete(this.generateToastKeyFromParameters(this.toasts[toastIndex].toastData));
        this.toasts.splice(toastIndex, 1);
    }

    public removeAll(force: boolean = false) {
        if (force) {
            this.toastsMap.clear();
            this.toasts = [];

            return;
        }

        const ignoredToasts = this.toasts.filter((toast) => toast.toastData.ignoreRemoval === true);
        this.toasts.forEach((toast) => {
            if (toast.toastData.ignoreRemoval !== true) {
                if (toast.timeoutId !== undefined) {
                    clearTimeout(toast.timeoutId);
                }

                const key = this.generateToastKeyFromParameters(toast.toastData);
                this.toastsMap.delete(key);
            }
        });

        // Keep only ignored toasts
        this.toasts = ignoredToasts;
    }

    public handleClick(toastIndex: number, toastHandler?: any) {
        if (toastHandler) {
            toastHandler();
            this.removeToast(toastIndex);
        }
    }

    private isToastDistinct(toast: Toast): boolean {
        const key = this.generateToastKeyFromParameters(toast);
        return !this.toastsMap.has(key);
    }

    private generateToastKeyFromParameters(toast: Toast): string {
        const coreProps = [toast.body, toast.severity, toast.type || 'simple', toast.position || 'center', toast.listType || 'none'];

        if (toast.args) {
            coreProps.push(JSON.stringify(toast.args));
        }

        if (toast.listArguments && toast.listArguments.length > 0) {
            coreProps.push(JSON.stringify(toast.listArguments));
        }

        if (toast.actionButtonText) {
            coreProps.push(toast.actionButtonText);
        }

        return coreProps.join('|');
    }
}
